import type { Object3D } from "three";
import * as React from "react";
import { logger } from "../utils/logger";

/**
 * objectRegistry
 * --------------
 * A tiny, React-friendly registry of live Three.js Object3D roots currently present in the scene.
 *
 * Goals:
 * - Keep React/Zustand stores serializable (no Three objects inside app state)
 * - Provide fast, read-only access for panels/tools to the live scene graph
 * - Integrate with React 18 via useSyncExternalStore for correct subscriptions
 *
 * API:
 * - setAll(map): replace the entire registry snapshot and notify subscribers
 * - register(name, obj): add/update one entry, notify subscribers
 * - unregister(name): remove one entry, notify subscribers
 * - clear(): clear all entries, notify subscribers
 * - getAll(): read the current snapshot (non-reactive)
 * - useObjectRegistry(): React hook to subscribe to the snapshot
 */

type Listener = () => void;

let map: Record<string, Object3D> = {};
const listeners = new Set<Listener>();

function emit() {
  listeners.forEach((l) => {
    try {
      l();
    } catch (error) {
      logger.warn("[objectRegistry] Listener threw during emit", error);
    }
  });
}

export function setAll(next: Record<string, Object3D>) {
  map = next || {};
  emit();
}

export function register(name: string, obj: Object3D) {
  if (!name || !obj) return;
  map = { ...map, [name]: obj };
  emit();
}

export function unregister(name: string) {
  if (!name) return;
  if (!(name in map)) return;
  const { [name]: _, ...rest } = map;
  map = rest;
  emit();
}

export function clear() {
  if (Object.keys(map).length === 0) return;
  map = {};
  emit();
}

export function getAll(): Record<string, Object3D> {
  return map;
}

export function subscribe(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function useObjectRegistry(): Record<string, Object3D> {
  const getSnapshot = React.useCallback(() => map, []);
  const subscribeFn = React.useCallback((cb: Listener) => subscribe(cb), []);
  // React 18+ guaranteed in this project; subscribe to external store safely
  return React.useSyncExternalStore(subscribeFn, getSnapshot, getSnapshot);
}
